import { Component, EventEmitter, Fragment, h, Prop, State, Event, Element, forceUpdate } from '@stencil/core';
import { alertController, modalController } from '@ionic/core';
import { FileService, PageAnalysis } from 'src/global/service/FileService';
import { Tag, User } from 'src/global/models';
import { DrawingPageDoc, TagData } from 'common';

@Component({
  tag: 'drawing-analysis-modal',
  //styleUrl: 'drawing-upload-modal.css',
  //shadow: true,
})
export class DrawingAnalysisModal {
  static async present(projectId: string, fileId: string, pageId: string, callback: () => void): Promise<void> {
    const modal = await modalController.create({
      component: 'drawing-analysis-modal',
      componentProps: { projectId, fileId, pageId },
      cssClass: 'modal-height-auto',
      showBackdrop: true,
      backdropDismiss: false,
    });

    modal.addEventListener('updated', (e: CustomEvent) => {
      callback();
      modal.dismiss();
    });

    modal.addEventListener('dismiss', () => {
      modal.dismiss();
    });

    modal.present();
  }

  @Element() hostElement: HTMLElement;

  @Prop() projectId: string;
  @Prop() fileId: string;
  @Prop() pageId: string;

  @State() exampleDrawingNumber: string;
  @State() batchSize: number = 10;
  @State() analysisError: string;
  @State() analysisFeedback: string;
  @State() pageAnalysis: PageAnalysis[];

  private pages: DrawingPageDoc[];
  private projectTags: TagData[];

  @Event() updated: EventEmitter;
  @Event() dismiss: EventEmitter;

  get user(): User {
    return globalThis.user;
  }

  async componentDidLoad() {
    const db = FileService.getFilePagesDb(this.projectId, this.fileId);
    this.pages = await db.getDocs();
    this.projectTags = await Tag.getTags(this.projectId);
    this.exampleDrawingNumber = this.pages.find(p => p.drawing_num && /[a-z]/i.test(p.drawing_num))?.drawing_num;
  }

  async analyseFilePages() {
    try {
      this.analysisError = '';
      this.analysisFeedback = 'Starting analysis';

      const pageAnalysis: PageAnalysis[] = [];
      for (var i = 0; i < this.pageIds.length; i += this.batchSize) {
        const batch: string[] = this.pageIds.slice(i, i + this.batchSize);
        this.analysisFeedback = this.pageId ? `Analysing page ${this.pageId}` : `Analysing pages ${i + 1} to ${i + batch.length} of ${this.pageIds.length}`;
        const analysis: PageAnalysis[] = await FileService.analyseFilePages(this.projectId, this.fileId, batch, this.exampleDrawingNumber);
        console.log(`analysis ${i + 1} to ${i + this.batchSize}`);
        pageAnalysis.push(...analysis);
        if (i + this.batchSize >= this.pageIds.length) {
          this.analysisFeedback = null;
          this.pageAnalysis = pageAnalysis;
        }
      }
    } catch (e) {
      this.analysisError = e.message;
    }
  }

  get pageIds() {
    if (this.pageId) return [this.pageId];
    return this.pages.map(page => page._id);
  }

  async acceptAnalysis() {
    this.pageAnalysis.forEach(analysis => {
      const tagNames = analysis.tags.map(tag => tag.replace('#', ''));
      const page = this.pages.find(p => p._id == analysis._id);
      page.title = analysis.title;
      page.description = analysis.description;
      page.drawing_num = analysis.drawingNum;
      page.tags = this.projectTags.filter(tag => tagNames.includes(tag.name));
    });
    FileService.savePages(this.projectId, this.fileId, this.pages);
    this.updated.emit();
  }
  render() {
    return (
      <Fragment>
        <ion-header>
          <ion-toolbar>
            <ion-button slot="start" class="ion-hide-md-up" fill="clear" onClick={() => this.dismiss.emit()}>
              <ion-icon slot="icon-only" name="arrow-back" style={{ zoom: 1.3 }} />
            </ion-button>
            <ion-title>{this.pageId ? 'Analyse Page' : 'Analyse Pages'}</ion-title>
            <ion-button slot="end" fill="clear" class="ion-hide-md-down" onclick={() => this.dismiss.emit()}>
              <ion-icon slot="icon-only" name="close" />
            </ion-button>
          </ion-toolbar>
        </ion-header>
        <ion-page class="ion-padding" style={{ 'overflow-y': 'auto', 'max-height': 'calc(100vh - 140px)' }}>
          {this.pageAnalysis ? (
            this.pageAnalysis.map(analysis => {
              const tagNames = analysis.tags.map(tag => tag.replace('#', ''));
              const tags = this.projectTags.filter(tag => tagNames.includes(tag.name));
              return (
                <div>
                  page {analysis._id} : {analysis.title} {analysis.drawingNum}
                  <p>{analysis.description}</p>
                  <div>
                    {tags.map(tag => (
                      <concourse-tag projectId={this.projectId} data={tag}></concourse-tag>
                    ))}
                  </div>
                </div>
              );
            })
          ) : [
            <ion-input
              label="Batch Size"
              label-placement="stacked"
              class="cc-margin-bottom"
              value={this.batchSize}
              onIonChange={e => (this.batchSize = e.detail.value)}
            ></ion-input>,
            <ion-input
              label="Example Drawing Number"
              label-placement="stacked"
              class="cc-margin-bottom"
              value={this.exampleDrawingNumber}
              onIonChange={e => (this.exampleDrawingNumber = e.detail.value)}
            ></ion-input>
          ]}
        </ion-page>
        <ion-footer class="ion-padding-horizontal">
          {this.pageAnalysis ? (
            <ion-toolbar>
              <ion-button slot="start" fill="outline" onClick={() => (this.pageAnalysis = null)}>
                <ion-label>reject</ion-label>
              </ion-button>
              <ion-button slot="end" onClick={() => this.acceptAnalysis()}>
                <ion-label>accept</ion-label>
              </ion-button>
            </ion-toolbar>
          ) : (
            <ion-toolbar>
              {this.analysisError ? (
                <ion-text color="danger">{this.analysisError}</ion-text>
              ) : this.analysisFeedback ? (
                [<ion-text>{this.analysisFeedback}</ion-text>, <ion-spinner slot="end" name="dots"></ion-spinner>]
              ) : (
                <ion-button slot="end" disabled={!this.exampleDrawingNumber} onClick={() => this.analyseFilePages()}>
                  <ion-label>analyse</ion-label>
                </ion-button>
              )}
            </ion-toolbar>
          )}
        </ion-footer>
      </Fragment>
    );
  }
}
